import { Fragment, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryBar,
  RegistryEmpty,
  RegistryExportButton,
  RegistryFilterSelect,
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
  RegistrySearchBox,
  RegistryThSortable,
  RegistryToolbar,
  paginationWindow,
} from '@shared/ui/RegistryShell';
import { downloadCsv } from '@shared/lib/downloadCsv';
import { useAppSelector } from '@app/hooks';
import { useListCorrespondenceQuery } from '@shared/api';
import type { Correspondence as CorrespondenceItem } from '@entities/legal/types';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { correspondenceSourceLabel, correspondenceStatusLabel } from '@shared/lib/statusLabels';
import { CorrespondenceCard } from './CorrespondenceCard';

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'received', label: 'Получено' },
  { value: 'classified', label: 'Классифицировано' },
  { value: 'assigned', label: 'Назначено' },
  { value: 'returned', label: 'Возвращено' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'Любой источник' },
  { value: 'dispatcher', label: 'Диспетчер' },
  { value: 'internal', label: 'Внутреннее' },
  { value: 'manual', label: 'Вручную' },
];

const SORT_OPTIONS = [
  { value: 'incoming_at', label: 'По дате' },
  { value: 'sender', label: 'По отправителю' },
  { value: 'status', label: 'По статусу' },
];

function formatDt(value: string): string {
  return new Date(value).toLocaleString('ru-RU');
}

/** Бейдж маршрутизации: куда LLM-классификатор отправил письмо.
 *  Берётся из `agent_classification.letter_type` — этого достаточно для
 *  списка, точная связка с конкретной карточкой реестра видна в карточке
 *  письма (linked_claim / linked_contract / linked_gov_request). */
function routeBadge(m: CorrespondenceItem) {
  const cls = m.agent_classification as { letter_type?: string } | null;
  const t = cls?.letter_type;
  if (!t) return <span style={{ color: 'var(--osnova-text-muted, #888)' }}>—</span>;
  switch (t) {
    case 'gov_request':
      return <Badge>→ Госорганы</Badge>;
    case 'pretension':
      return <Badge>→ Претензии</Badge>;
    case 'court_doc':
      return <Badge>→ Иски</Badge>;
    case 'bankruptcy':
      return <Badge>→ Банкротство</Badge>;
    case 'contract_draft':
      return <Badge>→ Договоры</Badge>;
    case 'corporate':
      return <Badge>корпоративное</Badge>;
    case 'other':
      return <Badge>прочее</Badge>;
    default:
      return <Badge>{t}</Badge>;
  }
}

export function Correspondence() {
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sort, setSort] = useState('incoming_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [includeReturned, setIncludeReturned] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, sourceFilter, sort, order, includeReturned]);

  const queryArg = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      source: sourceFilter || undefined,
      sort,
      order,
      include_returned: includeReturned || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, statusFilter, sourceFilter, sort, order, includeReturned, page],
  );

  const { data, isLoading, isFetching } = useListCorrespondenceQuery(queryArg);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [isExporting, setIsExporting] = useState(false);
  async function onExport() {
    setIsExporting(true);
    try {
      await downloadCsv(
        '/agents/statut/correspondence/export.csv',
        {
          status: statusFilter,
          source: sourceFilter,
          search,
          sort,
          order,
        },
        accessToken,
        'correspondence.csv',
      );
    } finally {
      setIsExporting(false);
    }
  }

  const [selected, setSelected] = useState<CorrespondenceItem | null>(null);

  function toggleSort(field: string) {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('desc');
    }
  }

  function sortArrow(field: string): string {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  }

  const pages = paginationWindow(page, totalPages);

  return (
    <>
      <Note>
        Входящие письма от агента-диспетчера. Поступают через IMAP-опрос секретарской почты,
        классифицируются юридическим агентом по теме (договор / претензия / госорган / прочее)
        и попадают в реестр со сгенерированным черновиком ответа. Письма, которые LLM
        отнесла к другим подразделениям, автоматически возвращаются диспетчеру и в этом
        списке скрыты — включите «Показать возвращённые», чтобы их увидеть.
      </Note>

      <RegistryToolbar>
        <RegistrySearchBox
          placeholder="Поиск по отправителю или теме…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <RegistryFilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          title="Фильтр по статусу"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          title="Фильтр по источнику"
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          title="Сортировка"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: 'var(--osnova-text-secondary, #666)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          title="Включить в выдачу письма, которые агент посчитал не относящимися к юр.отделу и автоматически вернул диспетчеру"
        >
          <input
            type="checkbox"
            checked={includeReturned}
            onChange={(e) => setIncludeReturned(e.target.checked)}
          />
          Показать возвращённые
        </label>
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${total}${isFetching ? ' · обновление…' : ''}`}
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 12,
            color: 'var(--osnova-text-muted, #888)',
          }}
        >
          {search && (
            <span>
              По запросу «<strong>{search}</strong>»{' · '}
              <button
                type="button"
                onClick={() => setSearchInput('')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                сбросить
              </button>
            </span>
          )}
          <RegistryExportButton
            type="button"
            onClick={onExport}
            disabled={isExporting || total === 0}
            $disabled={isExporting || total === 0}
            title="Скачать текущую выборку в CSV для Excel"
          >
            {isExporting ? 'Готовим…' : 'Экспорт CSV'}
          </RegistryExportButton>
        </span>
      </RegistryBar>

      <Card>
        <Table>
          <thead>
            <tr>
              <RegistryThSortable
                $active={sort === 'incoming_at'}
                onClick={() => toggleSort('incoming_at')}
              >
                Поступило{sortArrow('incoming_at')}
              </RegistryThSortable>
              <th>Источник</th>
              <RegistryThSortable $active={sort === 'sender'} onClick={() => toggleSort('sender')}>
                Отправитель{sortArrow('sender')}
              </RegistryThSortable>
              <th>Тема</th>
              <th>Маршрут</th>
              <RegistryThSortable $active={sort === 'status'} onClick={() => toggleSort('status')}>
                Статус{sortArrow('status')}
              </RegistryThSortable>
              <th>Возвращено</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <Skeleton $width={120} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={80} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width="70%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width="80%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={120} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width={40} $height={14} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <RegistryEmpty colSpan={7}>
                  {search || statusFilter || sourceFilter
                    ? 'По заданным фильтрам ничего не найдено.'
                    : 'Писем пока нет.'}
                </RegistryEmpty>
              </tr>
            ) : (
              items.map((m) => (
                <tr key={m.id} onClick={() => setSelected(m)} style={{ cursor: 'pointer' }}>
                  <td>{formatDt(m.incoming_at)}</td>
                  <td>
                    <Badge>{correspondenceSourceLabel(m.source)}</Badge>
                  </td>
                  <td>{m.sender ?? '—'}</td>
                  <td>{m.subject ?? '—'}</td>
                  <td>{routeBadge(m)}</td>
                  <td>
                    <Badge>{correspondenceStatusLabel(m.status)}</Badge>
                  </td>
                  <td>{m.returned_to_dispatcher ? 'да' : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {total > PAGE_SIZE && (
        <RegistryPagination>
          <span>
            Стр. {page + 1} из {totalPages} · показано {items.length} из {total}
          </span>
          <RegistryPageButtons>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Назад
            </RegistryPageBtn>
            {pages.map((p, idx) => {
              const prev = idx > 0 ? pages[idx - 1] : -1;
              const showGap = idx > 0 && p - prev > 1;
              return (
                <Fragment key={p}>
                  {showGap && <RegistryPageBtn disabled>…</RegistryPageBtn>}
                  <RegistryPageBtn type="button" $active={p === page} onClick={() => setPage(p)}>
                    {p + 1}
                  </RegistryPageBtn>
                </Fragment>
              );
            })}
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Вперёд →
            </RegistryPageBtn>
          </RegistryPageButtons>
        </RegistryPagination>
      )}

      {selected && (
        <CorrespondenceCard correspondenceId={selected.id} onClose={() => setSelected(null)} />
      )}
    </>
  );
}
